import { doNotification } from "../../../components/toast/nofitication";
import { handleError } from "../../../helperFunctions";
import { BaseAPIResponse } from "../../../utils/types";
import { baseApi } from "../../api";
import {
  CooperativeRolesResponse,
  CreateRolePayload,
  CreateRoleResponse,
  DeleteRolePayload,
  EditRolePayload,
  GetBankListResponse,
  GetCountriesResponse,
  GetStatesResponse,
  PermissionsResponse,
  ResolveAccountNumber,
} from "./settings.type";

export const settingsApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getStates: builder.query<GetStatesResponse, { country: string }>({
      query: (params) => {
        return {
          url: `/Settings/default/country/list/state`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    getCountries: builder.query<GetCountriesResponse, void>({
      query: () => {
        return {
          url: `/Settings/default/country/list/mobilecode`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    getBankList: builder.query<GetBankListResponse, void>({
      query: () => {
        return {
          url: `/Payment/Bank/list`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    resolveAccountNumber: builder.query<
      ResolveAccountNumber,
      {
        bankCode: string;
        accountNumber: string;
      }
    >({
      query: (params) => {
        return {
          url: `/Payment/Resolve/AccountNumber`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    getCooperativePermissions: builder.query<PermissionsResponse, void>({
      query: () => {
        return {
          url: `/AccessMgmt/defaultpermission`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    getCooperativeRoles: builder.query<
      CooperativeRolesResponse,
      {
        limit: number;
        offset: number;
      }
    >({
      query: (params) => {
        return {
          url: `/AccessMgmt/Role`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    createCooperativeRole: builder.mutation<
      CreateRoleResponse,
      CreateRolePayload
    >({
      query: (payload) => {
        return {
          url: `/AccessMgmt/Role`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    editCooperativeRole: builder.mutation<CreateRoleResponse, EditRolePayload>({
      query: (payload) => {
        return {
          url: `/AccessMgmt/Role`,
          method: "PUT",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          if (resp.data.success) {
            doNotification("Role updated successfully");
          }
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    deleteCooperativeRole: builder.mutation<BaseAPIResponse, DeleteRolePayload>(
      {
        query: (payload) => {
          return {
            url: `/AccessMgmt/Role`,
            method: "DELETE",
            body: payload,
          };
        },
        async onQueryStarted(arg, { queryFulfilled }) {
          try {
            const resp = await queryFulfilled;
            if (resp.data.success) {
              doNotification("Role deleted successfully");
            }
          } catch (err) {
            handleError(err, true);
          }
        },
      }
    ),
  }),
});

export const {
  useGetCountriesQuery,
  useGetStatesQuery,
  useGetBankListQuery,
  useResolveAccountNumberQuery,
  useGetCooperativePermissionsQuery,
  useGetCooperativeRolesQuery,
  useCreateCooperativeRoleMutation,
  useEditCooperativeRoleMutation,
  useDeleteCooperativeRoleMutation,
} = settingsApi;
